/************************************************************************************************
 * Copyright TRUSST AI PTY LTD. All Rights Reserved.                                            *
 *                                                                                              *
 * Licensed under the TRUSST SOFTWARE LICENSE (the "License"). You may not use this file except *
 * in compliance with the "LICENSE" file accompanying this file. This file is distributed       *
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, express or implied.       *
 *                                                                                              *
 * See the "License" file for the specific language governing permissions and limitations       *
 * under the License and limitations under the License.                                         *
 ***********************************************************************************************/

import {ContactImportStatus} from '@agent-assist/api-typescript-react-query-hooks';
import {Cancel, CheckCircleOutline} from '@mui/icons-material';
import {LinearProgress, Stack, Typography} from '@mui/material';

interface ContactImportProgressBarProps {
  readonly processedCount: number;
  readonly totalCount: number;
  readonly status: ContactImportStatus;
}

const ContactImportProgressBar = ({processedCount, totalCount, status}: ContactImportProgressBarProps) => {
  const progress = (processedCount / totalCount) * 100;

  switch (status) {
    case 'STARTED':
    case 'INITIALISING':
      return (
        <Stack direction={'row'} spacing={1}>
          <LinearProgress variant={'determinate'} value={0} sx={{flexGrow: 1}} />
          <Typography color={'text.secondary'}>{`0%`}</Typography>
        </Stack>
      );

    case 'IN_PROGRESS':
      return (
        <Stack direction={'row'} spacing={1}>
          <LinearProgress variant={'determinate'} value={progress ?? 0} sx={{flexGrow: 1}} />
          <Typography color={'text.secondary'}>{`${progress ? Math.round(progress) : 0}%`}</Typography>
        </Stack>
      );

    case 'PARTIAL_SUCCESS':
    case 'SUCCESS':
      return (
        <Stack direction={'row'} spacing={1}>
          <CheckCircleOutline color={'success'} />
          <Typography color={'success.main'}>{`${processedCount} of ${totalCount} imported`}</Typography>
        </Stack>
      );

    case 'FAILURE':
    default:
      return (
        <Stack direction={'row'} spacing={1}>
          <Cancel color={'error'} />
          <Typography color={'error.main'}>Import Job Failed</Typography>
        </Stack>
      );
  }
};

export default ContactImportProgressBar;
